import { useCallback, useMemo, useRef, useState } from 'react';

export const debounce = <Arguments extends unknown[]>(
  debouncedFunction: (...args: Arguments) => void,
  time: number
) => {
  let interval: number;
  return (...args: Arguments) => {
    window.clearTimeout(interval);
    interval = window.setTimeout(() => {
      debouncedFunction(...args);
    }, time);
  };
};

export const useDebounce = <Arguments extends unknown[]>(
  debouncedFunction: (...args: Arguments) => void,
  time: number
) => {
  const debouncedFunctionRef = useRef(debouncedFunction);
  debouncedFunctionRef.current = debouncedFunction;

  return useMemo(
    () => debounce((...args: Arguments) => debouncedFunctionRef.current(...args), time),
    [time]
  );
};

export function useDebouncedValue<ValueType>(
  initialValue: ValueType,
  onChange: (value: ValueType) => void,
  delay: number
): [ValueType, (value: ValueType) => void] {
  const [value, setValue] = useState(initialValue);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const debouncedOnChange = useMemo(
    () => debounce((value: ValueType) => onChangeRef.current(value), delay),
    [delay]
  );
  const handler = useCallback(
    (newValue: ValueType) => {
      debouncedOnChange(newValue);
      setValue(newValue);
    },
    [debouncedOnChange]
  );

  return [value, handler];
}
